import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { routes } from '@/app/routes'
import { sidebarSectionsFor } from '@/components/navigation/nav-items'
import { shellStrings as s } from '@/components/navigation/strings'
import { useUser } from '@/features/auth/useSession'
import { propertyApi } from '@/features/property/property.api'
import { tenantListApi } from '@/features/tenant/tenant.api'
import { leaseApi } from '@/features/lease/lease.api'
import { DsCommandPalette, DsIcons, useHotkey, type DsCommandGroup, type DsCommandItem } from '@/ds'
import { usePaymentLauncher } from '@/features/payments/usePaymentLauncher'
import { useHasVerification } from '@/features/capabilities/useCapabilities'

const SEARCH_MIN = 2
const SEARCH_LIMIT = 5

function useDebounced<T>(value: T, ms = 250): T {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return v
}

export function CommandMenu({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const user = useUser()
  const navigate = useNavigate()
  const pay = usePaymentLauncher()
  const [query, setQuery] = useState('')
  const q = useDebounced(query.trim())
  const landlord = user?.defaultProfile === 'landlord'
  const searching = open && landlord && q.length >= SEARCH_MIN

  useHotkey('mod+k', () => onOpenChange(!open), { allowInInputs: true })
  useHotkey('/', () => onOpenChange(true), { enabled: !open })
  useEffect(() => {
    if (!open) setQuery('')
  }, [open])

  const properties = useQuery({ queryKey: ['command', 'properties', q], queryFn: () => propertyApi.list({ page: 1, size: SEARCH_LIMIT, searchTerm: q }), enabled: searching, staleTime: 30_000 })
  const tenants = useQuery({ queryKey: ['command', 'tenants', q], queryFn: () => tenantListApi.list(q), enabled: searching, staleTime: 30_000 })
  const leases = useQuery({ queryKey: ['command', 'leases', q], queryFn: () => leaseApi.list({ page: 1, size: SEARCH_LIMIT, search: q }), enabled: searching, staleTime: 30_000 })

  // Hidden where the country offers neither verification nor payouts.
  const hasVerification = useHasVerification()

  const groups = useMemo<DsCommandGroup[]>(() => {
    if (!user) return []
    const go = (to: string) => () => navigate(to)
    const pages: DsCommandItem[] = sidebarSectionsFor(user.defaultProfile).flatMap((sec) => sec.items.map((i) => ({ id: `page:${i.to}`, label: i.label, icon: i.icon, description: sec.label, onSelect: go(i.to) })))
    const actions: DsCommandItem[] = landlord
      ? [
          { id: 'act:property', label: s.addProperty, icon: DsIcons.addProperty, keywords: ['new', 'create'], onSelect: go(routes.landlord.propertyNew) },
          { id: 'act:lease', label: s.createLease, icon: DsIcons.lease, keywords: ['new', 'tenant', 'add'], onSelect: go(routes.landlord.leaseNew) },
          { id: 'act:payment', label: s.recordPayment, icon: DsIcons.money, keywords: ['rent', 'cash', 'offline'], onSelect: () => pay.recordPayment() },
          ...(hasVerification ? [{ id: 'act:kyc', label: s.kyc, icon: DsIcons.idCard, keywords: ['bank', 'upi', 'payout'], onSelect: go(routes.kyc) }] : []),
          { id: 'act:notif', label: s.notificationSettings, icon: DsIcons.bell, onSelect: go(routes.accountNotifications) },
        ]
      : [
          { id: 'act:browse', label: s.browseProperties, icon: DsIcons.search, keywords: ['rent', 'find', 'home'], onSelect: go(routes.tenant.browse) },
          { id: 'act:interests', label: s.myInterests, icon: DsIcons.interest, keywords: ['interest', 'landlord', 'accepted', 'waiting'], onSelect: go(routes.tenant.interests) },
          { id: 'act:profile', label: s.editTenantProfile, icon: DsIcons.user, onSelect: go(routes.tenant.profile) },
          ...(hasVerification ? [{ id: 'act:kyc', label: s.kyc, icon: DsIcons.idCard, onSelect: go(routes.kyc) }] : []),
          { id: 'act:notif', label: s.notificationSettings, icon: DsIcons.bell, onSelect: go(routes.accountNotifications) },
        ]
    const out: DsCommandGroup[] = [
      { label: s.pages, items: pages },
      { label: s.actions, items: actions },
    ]
    if (searching) {
      out.push(
        { label: s.searchProperties, remote: true, loading: properties.isFetching, items: (properties.data?.properties ?? []).slice(0, SEARCH_LIMIT).map((p) => ({ id: `p:${p.id}`, label: p.title, description: [p.address?.locality, p.address?.city].filter(Boolean).join(', '), icon: DsIcons.house, onSelect: go(routes.landlord.property(p.id)) })) },
        { label: s.searchTenants, remote: true, loading: tenants.isFetching, items: (tenants.data ?? []).slice(0, SEARCH_LIMIT).map((t) => ({ id: `t:${t.id}`, label: [t.firstName, t.lastName].filter(Boolean).join(' ') || t.mobile || '—', description: t.property?.title ?? t.mobile, icon: DsIcons.user, onSelect: go(t.leaseIds?.[0] ? routes.lease(t.leaseIds[0]) : routes.landlord.tenants) })) },
        { label: s.searchLeases, remote: true, loading: leases.isFetching, items: (leases.data?.items ?? []).slice(0, SEARCH_LIMIT).map((l) => ({ id: `l:${l.id}`, label: l.property?.title ?? '—', description: [l.tenant?.firstName, l.tenant?.lastName].filter(Boolean).join(' '), icon: DsIcons.lease, onSelect: go(routes.lease(l.id)) })) },
      )
    }
    return out
  }, [user, landlord, hasVerification, navigate, pay, searching, properties.data, properties.isFetching, tenants.data, tenants.isFetching, leases.data, leases.isFetching])

  if (!user) return null
  return <DsCommandPalette open={open} onOpenChange={onOpenChange} groups={groups} query={query} onQueryChange={setQuery} placeholder={s.search} emptyLabel={s.noMatches} />
}
